import React from 'react';

// WP4-B — Android "Add to Home Screen" walkthrough.
//
// Illustrated step diagrams instead of real-device screenshots: each step is a
// small, on-brand phone illustration with the relevant Chrome UI highlighted in
// gold, plus a numbered caption. Drawn UI renders crisp at any density and
// stays accurate across Chrome versions (the wording lives in the captions, the
// art just points at where to tap). Shown inline when an Android user taps
// "Add this to your Home Screen" in the Play footer.

export default function A2HSWalkthrough() {
  return (
    <div style={wrap}>
      <p style={heading}>Add Predict Six to your Home Screen</p>
      <p style={sub}>
        Takes about five seconds. It’ll open in its own window — just like an app.
      </p>

      <ol style={list}>
        {STEPS.map((s) => (
          <li key={s.n} style={row}>
            <div style={artBox}>{s.art}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={stepHead}>
                <span style={badge}>{s.n}</span>
                <span style={stepTitle}>{s.title}</span>
              </div>
              <p style={stepBody}>{s.body}</p>
            </div>
          </li>
        ))}
      </ol>

      <p style={footnote}>
        Don’t see “Add to Home screen”? Some phones label it{' '}
        <b>“Install app”</b> — same thing. Make sure you’re in <b>Chrome</b> (not
        an in-app browser); if a banner offers to install Predict Six, just tap it.
      </p>
    </div>
  );
}

const STEPS = [
  {
    n: 1,
    title: 'Open the browser menu',
    body: 'Tap the ⋮ menu in the top-right corner of Chrome.',
    art: <StepMenu />,
  },
  {
    n: 2,
    title: 'Tap “Add to Home screen”',
    body: 'Choose it from the menu — it may also read “Install app.”',
    art: <StepAdd />,
  },
  {
    n: 3,
    title: 'Confirm',
    body: 'When the prompt slides up, tap “Add” (or “Install”).',
    art: <StepConfirm />,
  },
  {
    n: 4,
    title: 'You’re set',
    body: 'The Predict Six icon lands on your home screen — one tap back in, every morning.',
    art: <StepHome />,
  },
];

/* ── Illustrations ─────────────────────────────────────────────────────────
   All share a 120×180 portrait phone frame, rendered ~92px wide. Screen
   contents are clipped to the rounded screen rect (unique clip id per step). */

function StepMenu() {
  return (
    <Phone clip="a2hs-scr1" label="Tap the menu in the top-right of your browser">
      {/* Chrome top bar */}
      <rect x="18" y="13" width="84" height="20" fill="var(--surface-light)" />
      <rect
        x="23"
        y="18"
        width="55"
        height="9"
        rx="4.5"
        fill="var(--card-bg)"
        stroke="var(--border)"
        strokeWidth="0.75"
      />
      <circle cx="27.5" cy="22.5" r="1.4" fill="var(--secondary-text)" />
      <rect x="31" y="21" width="41" height="3" rx="1.5" fill="var(--secondary-text)" opacity="0.45" />
      {/* Kebab menu, highlighted */}
      <circle cx="92" cy="23" r="8" fill="var(--gold)" opacity="0.16" />
      <circle cx="92" cy="19.6" r="1.3" fill="var(--primary-text)" />
      <circle cx="92" cy="23" r="1.3" fill="var(--primary-text)" />
      <circle cx="92" cy="26.4" r="1.3" fill="var(--primary-text)" />
      <circle cx="92" cy="23" r="11" fill="none" stroke="var(--gold)" strokeWidth="1.5" />
      {/* Page content */}
      <text
        x="60"
        y="80"
        fontSize="11"
        fontWeight="900"
        fill="var(--gold)"
        textAnchor="middle"
        fontFamily="var(--font)"
      >
        Predict Six
      </text>
      <rect x="26" y="92" width="68" height="13" rx="4" fill="var(--surface)" />
      <rect x="26" y="110" width="68" height="13" rx="4" fill="var(--surface)" />
      <rect x="26" y="128" width="68" height="13" rx="4" fill="var(--surface)" />
    </Phone>
  );
}

function StepAdd() {
  return (
    <Phone clip="a2hs-scr2" label="Choose Add to Home screen from the menu">
      {/* Dimmed page behind the open menu */}
      <rect x="18" y="13" width="84" height="154" fill="var(--primary-text)" opacity="0.08" />
      {/* Dropdown menu anchored top-right */}
      <rect
        x="44"
        y="16"
        width="56"
        height="84"
        rx="5"
        fill="var(--card-bg)"
        stroke="var(--border)"
        strokeWidth="1"
      />
      {/* Plain rows */}
      {[26, 40, 80, 92].map((y) => (
        <g key={y}>
          <circle cx="51" cy={y} r="2.1" fill="var(--secondary-text)" opacity="0.4" />
          <rect x="57" y={y - 1.5} width="35" height="3" rx="1.5" fill="var(--secondary-text)" opacity="0.35" />
        </g>
      ))}
      {/* Highlighted "Add to Home screen" row */}
      <rect x="45" y="52" width="54" height="14" rx="3" fill="var(--gold)" opacity="0.18" />
      {/* little house-with-plus glyph */}
      <path d="M48 60.5 L51.5 57.5 L55 60.5 L55 63.5 L48 63.5 Z" fill="none" stroke="var(--gold)" strokeWidth="1.2" strokeLinejoin="round" />
      <line x1="51.5" y1="53.5" x2="51.5" y2="56" stroke="var(--gold)" strokeWidth="1.2" strokeLinecap="round" />
      <line x1="50.2" y1="54.7" x2="52.8" y2="54.7" stroke="var(--gold)" strokeWidth="1.2" strokeLinecap="round" />
      <rect x="60" y="57.5" width="33" height="3.4" rx="1.7" fill="var(--gold)" />
      {/* tap indicator */}
      <circle cx="80" cy="59" r="9" fill="none" stroke="var(--gold)" strokeWidth="1.4" opacity="0.7" />
    </Phone>
  );
}

function StepConfirm() {
  return (
    <Phone clip="a2hs-scr3" label="Tap Add to confirm">
      {/* Dimmed backdrop */}
      <rect x="18" y="13" width="84" height="154" fill="var(--primary-text)" opacity="0.28" />
      {/* Bottom sheet */}
      <rect x="18" y="104" width="84" height="63" fill="var(--card-bg)" />
      <rect x="54" y="109" width="12" height="2.4" rx="1.2" fill="var(--secondary-text)" opacity="0.6" />
      {/* App icon */}
      <rect x="26" y="118" width="22" height="22" rx="6" fill="var(--gold)" />
      <text
        x="37"
        y="134.5"
        fontSize="13"
        fontWeight="900"
        fill="#ffffff"
        textAnchor="middle"
        fontFamily="var(--font)"
      >
        6
      </text>
      {/* Name + url */}
      <rect x="54" y="121" width="34" height="4" rx="2" fill="var(--primary-text)" opacity="0.8" />
      <rect x="54" y="130" width="26" height="3" rx="1.5" fill="var(--secondary-text)" opacity="0.5" />
      {/* Add button, highlighted */}
      <rect x="66" y="148" width="30" height="13" rx="6.5" fill="var(--gold)" />
      <text
        x="81"
        y="157"
        fontSize="6.5"
        fontWeight="800"
        fill="#ffffff"
        textAnchor="middle"
        fontFamily="var(--font)"
      >
        Add
      </text>
      <rect x="62" y="144" width="38" height="21" rx="9" fill="none" stroke="var(--gold)" strokeWidth="1.4" opacity="0.65" />
    </Phone>
  );
}

function StepHome() {
  // A few placeholder home-screen icons + the highlighted Predict Six icon.
  const placeholders = [
    [30, 44], [56, 44], [82, 44],
    [30, 72], [82, 72],
  ];
  return (
    <Phone clip="a2hs-scr4" label="The Predict Six icon on your home screen">
      {/* Status bar dots */}
      <circle cx="24" cy="20" r="1.2" fill="var(--secondary-text)" opacity="0.5" />
      <rect x="88" y="18.5" width="10" height="3" rx="1.5" fill="var(--secondary-text)" opacity="0.4" />
      {placeholders.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width="18" height="18" rx="5" fill="var(--surface-light)" />
      ))}
      {/* Predict Six icon, highlighted (centered so its label fits) */}
      <rect x="56" y="72" width="18" height="18" rx="5" fill="var(--gold)" />
      <text
        x="65"
        y="85.5"
        fontSize="11"
        fontWeight="900"
        fill="#ffffff"
        textAnchor="middle"
        fontFamily="var(--font)"
      >
        6
      </text>
      <rect x="54" y="68" width="22" height="22" rx="7" fill="none" stroke="var(--gold)" strokeWidth="1.6" />
      <text
        x="65"
        y="98"
        fontSize="5.5"
        fontWeight="700"
        fill="var(--primary-text)"
        textAnchor="middle"
        fontFamily="var(--font)"
      >
        Predict Six
      </text>
      {/* sparkle at the icon's top-right */}
      <path d="M77 66 l1.1 2.4 2.4 1.1 -2.4 1.1 -1.1 2.4 -1.1 -2.4 -2.4 -1.1 2.4 -1.1 Z" fill="var(--pure-gold)" />
    </Phone>
  );
}

// Shared phone frame + screen clip.
function Phone({ clip, label, children }) {
  return (
    <svg
      viewBox="0 0 120 180"
      style={{ width: 92, height: 'auto', display: 'block' }}
      role="img"
      aria-label={label}
    >
      <defs>
        <clipPath id={clip}>
          <rect x="18" y="13" width="84" height="154" rx="6" />
        </clipPath>
      </defs>
      <rect x="12" y="4" width="96" height="172" rx="16" fill="var(--card-bg)" stroke="var(--border)" strokeWidth="2" />
      <rect x="18" y="13" width="84" height="154" rx="6" fill="var(--bg)" />
      <g clipPath={`url(#${clip})`}>{children}</g>
    </svg>
  );
}

/* ── styles ──────────────────────────────────────────────────────────────── */
const wrap = {
  marginTop: 6,
  marginBottom: 10,
  padding: '14px 14px 4px',
  background: 'var(--surface)',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
};
const heading = {
  margin: '0 0 4px',
  fontSize: 15,
  fontWeight: 800,
  color: 'var(--primary-text)',
  textAlign: 'center',
};
const sub = {
  margin: '0 0 14px',
  fontSize: 12.5,
  color: 'var(--secondary-text)',
  textAlign: 'center',
  lineHeight: 1.45,
};
const list = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};
const row = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
};
const artBox = {
  flex: '0 0 92px',
  display: 'flex',
  justifyContent: 'center',
};
const stepHead = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 3,
};
const badge = {
  flex: '0 0 auto',
  width: 20,
  height: 20,
  borderRadius: '50%',
  background: 'var(--gold)',
  color: '#fff',
  fontSize: 12,
  fontWeight: 800,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
};
const stepTitle = {
  fontSize: 14.5,
  fontWeight: 800,
  color: 'var(--primary-text)',
};
const stepBody = {
  margin: 0,
  fontSize: 13,
  lineHeight: 1.45,
  color: 'var(--secondary-text)',
};
const footnote = {
  margin: '14px 0 0',
  padding: '10px 0 0',
  borderTop: '1px solid var(--border)',
  fontSize: 12,
  lineHeight: 1.5,
  color: 'var(--secondary-text)',
};
